/**
 * @file Components/Admin/SiteContent.jsx
 * @description Admin panel for managing all dynamic website content:
 * Hero section, Company info, Footer, Social links, SEO, Campus Ambassador.
 */

import React, { useState, useEffect } from 'react';
import { Save, Loader2, Plus, X, Image, Globe, Building2, MapPin, Share2, Search, GraduationCap, LayoutDashboard } from 'lucide-react';
import { toast } from 'react-toastify';
import { getSiteSettings, updateSiteSettings } from '../../services/api';
import { useSiteSettings } from '../../Context/SiteSettingsContext';
import ImageUploader from '../Ui/ImageUploader';

const TABS = [
  { id: 'hero', label: 'Hero Section', icon: Image },
  { id: 'company', label: 'Company Info', icon: Building2 },
  { id: 'footer', label: 'Footer', icon: LayoutDashboard },
  { id: 'social', label: 'Social Links', icon: Share2 },
  { id: 'seo', label: 'SEO', icon: Search },
  { id: 'campus', label: 'Campus Ambassador', icon: GraduationCap },
];

// ─── Reusable link-list editor ──────────────────────────────────────
const LinkListEditor = ({ label, items, onChange }) => {
  const add = () => onChange([...items, { label: '', url: '' }]);
  const remove = (i) => onChange(items.filter((_, j) => j !== i));
  const update = (i, field, value) => {
    const copy = [...items];
    copy[i] = { ...copy[i], [field]: value };
    onChange(copy);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-semibold text-gray-700">{label}</label>
        <button type="button" onClick={add} className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
          <Plus size={12} /> Add
        </button>
      </div>
      {items.map((item, i) => (
        <div key={i} className="flex gap-2 mb-2">
          <input type="text" placeholder="Label" value={item.label || ''} onChange={(e) => update(i, 'label', e.target.value)}
            className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm outline-none" />
          <input type="text" placeholder="URL (e.g. /tours)" value={item.url || ''} onChange={(e) => update(i, 'url', e.target.value)}
            className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm outline-none" />
          <button type="button" onClick={() => remove(i)} className="text-red-400 hover:text-red-600"><X size={16} /></button>
        </div>
      ))}
      {items.length === 0 && <p className="text-xs text-gray-400">No items yet.</p>}
    </div>
  );
};

// ─── Badge editor (value + label pairs) ─────────────────────────────
const BadgeEditor = ({ label, items, onChange }) => {
  const add = () => onChange([...items, { value: '', label: '' }]);
  const remove = (i) => onChange(items.filter((_, j) => j !== i));
  const update = (i, field, val) => {
    const copy = [...items];
    copy[i] = { ...copy[i], [field]: val };
    onChange(copy);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-semibold text-gray-700">{label}</label>
        <button type="button" onClick={add} className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
          <Plus size={12} /> Add
        </button>
      </div>
      {items.map((item, i) => (
        <div key={i} className="flex gap-2 mb-2">
          <input type="text" placeholder="Value (e.g. 500+)" value={item.value || ''} onChange={(e) => update(i, 'value', e.target.value)}
            className="w-32 px-3 py-1.5 border border-gray-300 rounded-lg text-sm outline-none" />
          <input type="text" placeholder="Label (e.g. Happy Travelers)" value={item.label || ''} onChange={(e) => update(i, 'label', e.target.value)}
            className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm outline-none" />
          <button type="button" onClick={() => remove(i)} className="text-red-400 hover:text-red-600"><X size={16} /></button>
        </div>
      ))}
    </div>
  );
};

// ─── Field helper ───────────────────────────────────────────────────
const Field = ({ label, value, onChange, type = 'text', placeholder, rows, hint }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {rows ? (
      <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={rows} placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none resize-none" />
    ) : (
      <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none" />
    )}
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

// ═════════════════════════════════════════════════════════════════════
const SiteContent = () => {
  const { refetch } = useSiteSettings();
  const [activeTab, setActiveTab] = useState('hero');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await getSiteSettings();
        const s = res?.data?.data?.settings || res?.data?.settings;
        if (s) setData(s);
      } catch (err) {
        toast.error('Failed to load site settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const payload = {
        companyInfo: data.companyInfo,
        hero: data.hero,
        socialLinks: data.socialLinks,
        footer: data.footer,
        seo: data.seo,
        campusAmbassador: data.campusAmbassador,
        contactForm: data.contactForm,
      };
      await updateSiteSettings(payload);
      toast.success('Site content saved!');
      refetch();
    } catch (err) {
      toast.error('Failed to save: ' + (err?.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  // Deep setter: setNested('hero', 'heading', 'New Title')
  const setNested = (section, field, value) => {
    setData(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  };
  // Deeper setter: setDeepNested('hero', 'ctaPrimary', 'text', 'Go')
  const setDeepNested = (section, sub, field, value) => {
    setData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [sub]: { ...prev[section]?.[sub], [field]: value },
      },
    }));
  };
  // Address setter
  const setAddress = (field, value) => {
    setData(prev => ({
      ...prev,
      companyInfo: {
        ...prev.companyInfo,
        address: { ...prev.companyInfo?.address, [field]: value },
      },
    }));
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin h-8 w-8 text-indigo-500" /></div>;
  }
  if (!data) {
    return <div className="text-center text-red-500 py-10">Failed to load settings.</div>;
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Site Content Manager</h2>
          <p className="text-sm text-gray-500">Control every section of your website from here.</p>
        </div>
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium text-sm">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b mb-6 overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === tab.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              <Icon size={16} /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* ═══════ HERO TAB ═══════ */}
      {activeTab === 'hero' && (
        <div className="bg-white rounded-xl shadow-sm border p-6 space-y-5">
          <h3 className="font-semibold text-gray-900 border-b pb-2">Hero Section</h3>
          <ImageUploader label="Background Image" value={data.hero?.backgroundImage} onChange={(v) => setNested('hero', 'backgroundImage', v)}
            hint="Use a high-resolution image (1920x1080+). Upload from your computer or paste a URL." />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Heading (Line 1)" value={data.hero?.heading} onChange={(v) => setNested('hero', 'heading', v)} placeholder="Best Inbound & Outbound" />
            <Field label="Heading Highlight (Line 2)" value={data.hero?.headingHighlight} onChange={(v) => setNested('hero', 'headingHighlight', v)} placeholder="Tour Operator in India" />
          </div>
          <Field label="Subtitle" value={data.hero?.subtitle} onChange={(v) => setNested('hero', 'subtitle', v)} rows={2} />
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
              <p className="text-xs font-semibold text-gray-500 uppercase">Primary CTA Button</p>
              <Field label="Button Text" value={data.hero?.ctaPrimary?.text} onChange={(v) => setDeepNested('hero', 'ctaPrimary', 'text', v)} />
              <Field label="Button Link" value={data.hero?.ctaPrimary?.link} onChange={(v) => setDeepNested('hero', 'ctaPrimary', 'link', v)} placeholder="/tours" />
            </div>
            <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
              <p className="text-xs font-semibold text-gray-500 uppercase">Secondary CTA Button</p>
              <Field label="Button Text" value={data.hero?.ctaSecondary?.text} onChange={(v) => setDeepNested('hero', 'ctaSecondary', 'text', v)} />
              <Field label="Button Link" value={data.hero?.ctaSecondary?.link} onChange={(v) => setDeepNested('hero', 'ctaSecondary', 'link', v)} placeholder="/blogs" />
            </div>
          </div>
        </div>
      )}

      {/* ═══════ COMPANY INFO TAB ═══════ */}
      {activeTab === 'company' && (
        <div className="bg-white rounded-xl shadow-sm border p-6 space-y-5">
          <h3 className="font-semibold text-gray-900 border-b pb-2">Company Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Company Name" value={data.companyInfo?.name} onChange={(v) => setNested('companyInfo', 'name', v)} />
            <Field label="Tagline (Header)" value={data.companyInfo?.tagline} onChange={(v) => setNested('companyInfo', 'tagline', v)}
              hint="Displayed in the main header bar" />
          </div>
          <Field label="Description" value={data.companyInfo?.description} onChange={(v) => setNested('companyInfo', 'description', v)} rows={3} />
          <div className="grid grid-cols-3 gap-4">
            <Field label="Email" value={data.companyInfo?.email} onChange={(v) => setNested('companyInfo', 'email', v)} type="email" />
            <Field label="Phone" value={data.companyInfo?.phone} onChange={(v) => setNested('companyInfo', 'phone', v)} placeholder="+91 800 2406 800" />
            <Field label="WhatsApp Number" value={data.companyInfo?.whatsapp} onChange={(v) => setNested('companyInfo', 'whatsapp', v)}
              hint="Without + sign, e.g. 918002406800" />
          </div>
          <div className="border-t pt-4">
            <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><MapPin size={16} /> Address</p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Street" value={data.companyInfo?.address?.street} onChange={(v) => setAddress('street', v)} />
              <Field label="City" value={data.companyInfo?.address?.city} onChange={(v) => setAddress('city', v)} />
              <Field label="State" value={data.companyInfo?.address?.state} onChange={(v) => setAddress('state', v)} />
              <Field label="Country" value={data.companyInfo?.address?.country} onChange={(v) => setAddress('country', v)} />
              <Field label="Postal Code" value={data.companyInfo?.address?.postalCode} onChange={(v) => setAddress('postalCode', v)} />
            </div>
          </div>
          <div className="border-t pt-4">
            <p className="text-sm font-semibold text-gray-700 mb-3">Contact Form Settings</p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Recipient Email" value={data.contactForm?.recipientEmail} onChange={(v) => setNested('contactForm', 'recipientEmail', v)} />
              <Field label="Auto-Reply Subject" value={data.contactForm?.autoReplySubject} onChange={(v) => setNested('contactForm', 'autoReplySubject', v)} />
            </div>
            <div className="mt-3">
              <Field label="Auto-Reply Message" value={data.contactForm?.autoReplyMessage} onChange={(v) => setNested('contactForm', 'autoReplyMessage', v)} rows={2} />
            </div>
          </div>
        </div>
      )}

      {/* ═══════ FOOTER TAB ═══════ */}
      {activeTab === 'footer' && (
        <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
          <h3 className="font-semibold text-gray-900 border-b pb-2">Footer Content</h3>
          <Field label="About Text" value={data.footer?.aboutText} onChange={(v) => setNested('footer', 'aboutText', v)} rows={3}
            hint="Displayed in the footer's company info section" />
          <Field label="Copyright Text" value={data.footer?.copyrightText} onChange={(v) => setNested('footer', 'copyrightText', v)}
            hint="Use {year} for auto current year" />
          <div className="border-t pt-4">
            <LinkListEditor label="Quick Links" items={data.footer?.quickLinks || []}
              onChange={(v) => setNested('footer', 'quickLinks', v)} />
          </div>
          <div className="border-t pt-4">
            <LinkListEditor label="Popular Destinations" items={data.footer?.popularDestinations || []}
              onChange={(v) => setNested('footer', 'popularDestinations', v)} />
          </div>
          <div className="border-t pt-4">
            <LinkListEditor label="Bottom Bar Links" items={data.footer?.bottomLinks || []}
              onChange={(v) => setNested('footer', 'bottomLinks', v)} />
          </div>
          <div className="border-t pt-4">
            <BadgeEditor label="Trust Badges (Stats)" items={data.footer?.trustBadges || []}
              onChange={(v) => setNested('footer', 'trustBadges', v)} />
          </div>
        </div>
      )}

      {/* ═══════ SOCIAL LINKS TAB ═══════ */}
      {activeTab === 'social' && (
        <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
          <h3 className="font-semibold text-gray-900 border-b pb-2">Social Media Links</h3>
          <Field label="Facebook" value={data.socialLinks?.facebook} onChange={(v) => setNested('socialLinks', 'facebook', v)} placeholder="https://facebook.com/..." />
          <Field label="Instagram" value={data.socialLinks?.instagram} onChange={(v) => setNested('socialLinks', 'instagram', v)} placeholder="https://instagram.com/..." />
          <Field label="Twitter / X" value={data.socialLinks?.twitter} onChange={(v) => setNested('socialLinks', 'twitter', v)} placeholder="https://twitter.com/..." />
          <Field label="LinkedIn" value={data.socialLinks?.linkedin} onChange={(v) => setNested('socialLinks', 'linkedin', v)} placeholder="https://linkedin.com/..." />
          <Field label="YouTube" value={data.socialLinks?.youtube} onChange={(v) => setNested('socialLinks', 'youtube', v)} placeholder="https://youtube.com/..." />
        </div>
      )}

      {/* ═══════ SEO TAB ═══════ */}
      {activeTab === 'seo' && (
        <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
          <h3 className="font-semibold text-gray-900 border-b pb-2">SEO Defaults</h3>
          <Field label="Default Page Title" value={data.seo?.defaultTitle} onChange={(v) => setNested('seo', 'defaultTitle', v)}
            hint="Max 60 characters recommended" />
          <Field label="Default Meta Description" value={data.seo?.defaultDescription} onChange={(v) => setNested('seo', 'defaultDescription', v)} rows={2}
            hint="Max 160 characters recommended" />
          <Field label="Default Keywords" value={data.seo?.defaultKeywords} onChange={(v) => setNested('seo', 'defaultKeywords', v)} rows={2}
            hint="Comma-separated keywords" />
          <div className="grid grid-cols-2 gap-4 border-t pt-4">
            <Field label="Google Analytics ID" value={data.seo?.googleAnalyticsId} onChange={(v) => setNested('seo', 'googleAnalyticsId', v)} placeholder="G-XXXXXXXXXX" />
            <Field label="Google Tag Manager ID" value={data.seo?.googleTagManagerId} onChange={(v) => setNested('seo', 'googleTagManagerId', v)} placeholder="GTM-XXXXXXX" />
          </div>
          {/* Live Google preview */}
          <div className="border-t pt-4">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Google Search Preview</p>
            <div className="border rounded-lg p-4 bg-gray-50 max-w-lg">
              <p className="text-blue-700 text-lg font-medium truncate">{data.seo?.defaultTitle || 'Page Title'}</p>
              <p className="text-green-700 text-sm">https://nikabatravels.com</p>
              <p className="text-gray-600 text-sm line-clamp-2 mt-1">{data.seo?.defaultDescription || 'Meta description will appear here.'}</p>
            </div>
          </div>
        </div>
      )}

      {/* ═══════ CAMPUS AMBASSADOR TAB ═══════ */}
      {activeTab === 'campus' && (
        <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
          <h3 className="font-semibold text-gray-900 border-b pb-2">Campus Ambassador Program</h3>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="campusEnabled" checked={data.campusAmbassador?.enabled ?? true}
              onChange={(e) => setNested('campusAmbassador', 'enabled', e.target.checked)} className="rounded" />
            <label htmlFor="campusEnabled" className="text-sm font-medium text-gray-700">Show Campus Ambassador section on homepage</label>
          </div>
          <Field label="Section Heading" value={data.campusAmbassador?.heading} onChange={(v) => setNested('campusAmbassador', 'heading', v)} />
          <Field label="Description" value={data.campusAmbassador?.description} onChange={(v) => setNested('campusAmbassador', 'description', v)} rows={3} />
          <Field label="Google Form Link" value={data.campusAmbassador?.formLink} onChange={(v) => setNested('campusAmbassador', 'formLink', v)}
            hint="Link to the campus ambassador sign-up form" />
        </div>
      )}

      {/* Floating save button for mobile */}
      <div className="fixed bottom-6 right-6 md:hidden">
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 bg-indigo-600 text-white px-5 py-3 rounded-full shadow-xl hover:bg-indigo-700 disabled:opacity-50">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
          Save
        </button>
      </div>
    </div>
  );
};

export default SiteContent;
